import { HeaderV3 } from "@circle-react-shared/uikit/HeaderV3";
import { CircleAIAppShell } from "@circle-react-uikit/CircleAIAppShell";
import { AppBanner } from "@circle-react/components/AppBanner";
import { useIsCircleAiEnabled } from "@circle-react/components/CircleAi/useCircleAiFeatureFlags";
import { useCircleAiStore } from "@circle-react/components/CircleAi/useCircleAiStore";
import {
  useIsCommunityAdmin,
  useIsAdminOrModerator,
  usePunditUserContext,
} from "@circle-react/contexts/punditUserContext";
import { chatRoomsPath, twoFaPath } from "@circle-react/helpers/urlHelpers";
import { useLocation } from "@tanstack/react-router";
import { Suspense, type ReactNode } from "react";

import { t } from "@/i18n-js/instance";
import { AvatarChatActivitySync } from "@/react/components/Avatars/AvatarChatActivitySync";
import { AvatarConversationPanel } from "@/react/components/Avatars/conversationPanel/AvatarConversationPanel";
import { Root as AvatarPanelRoot } from "@/react/components/Avatars/conversationPanel/Root";
import { useAvatarPanelRouteState } from "@/react/components/Avatars/conversationPanel/useAvatarPanelRouteState";
import { useAvatarPanelUrlEntry } from "@/react/components/Avatars/conversationPanel/useAvatarPanelUrlEntry";
import { LatestAvatarChatButton } from "@/react/components/Avatars/LatestAvatarChatButton";
import { useAvatarConversationPanelStore } from "@/react/components/Avatars/useAvatarConversationPanelStore";
import { useIsAvatarsV2Layout } from "@/react/components/Avatars/useIsAvatarsV2Layout";
import { useIsAdminDockNavVisible } from "@/react/components/CircleAi/Dock/useIsAdminDockNavVisible";
import { useIsCircleAIDockNavEnabled } from "@/react/components/CircleAi/Dock/useIsCircleAIDockNavEnabled";
import { useIsCircleAiPageRoute } from "@/react/components/CircleAi/useIsCircleAiPageRoute";
import { useShellReveal } from "@/react/components/CircleAi/useShellReveal";
import { useCircleAiIntroCloseTrigger } from "@/react/components/CommunitySwitcher/useCircleAiIntroCloseTrigger";
import { useIsAdminArea } from "@/react/components/SettingsApp/CompassCompat/useIsAdminArea";
import { ErrorBoundary } from "@/react/components/shared/ErrorPages/ErrorBoundary";
import { lazyWithRetry } from "@/react/helpers/lazyRetry";
import { useBackendProps } from "@/react/providers/AppContext";
import { useRouteHelpers } from "@/react/routes/Main/useRouteHelpers";

import { AnalyticsReportCanvasSkeleton } from "../../CircleAi/Home/analytics/AnalyticsReportCanvasSkeleton";
import { MobileDrawerProvider } from "../MobileDrawer/mobileDrawerContext";
import { PlanTrialTopBanner } from "../StandardLayoutV2/PlanTrialTopBanner";
import { TrialBannerV2 } from "../StandardLayoutV2/TrialBannerV2";
import { AiConversationPanel } from "./AiConversationPanel";
import { CanvasHeaderProvider } from "./CanvasHeader/CanvasHeaderContext";
import { CanvasOnlyContent } from "./CanvasOnlyContent";
import { DesktopDock } from "./DesktopDock";
import { OnboardingHeader } from "./OnboardingHeader";
import {
  useIsPageChromeHidden,
  useIsPromoLockedToPlansPage,
  useIsShellBypassed,
} from "./ShellBypassContext";
import { ShellChromeBanners } from "./ShellChromeBanners";
import { ShellNativeContent } from "./ShellNativeContent";
import { StoreConnectedShellLayout } from "./StoreConnectedShellLayout";
import { useAutoOpenPanelOnRailbarContent } from "./useAutoOpenPanelOnRailbarContent";
import { useDesktopDockState } from "./useDesktopDockState";
import { useRouteOwnedShellState } from "./useRouteOwnedShellState";

const AnalyticsReportCanvas = lazyWithRetry(() =>
  import(
    /* webpackChunkName: "AnalyticsReportCanvas" */ "@circle-react/components/CircleAi/Home/analytics/AnalyticsReportCanvas"
  ).then(module => ({ default: module.AnalyticsReportCanvas })),
);

export interface CircleAIAppShellLayoutProps {
  children: ReactNode;
  hasMinimalLayout?: boolean;
  shouldDisplayHeader?: boolean;
}

interface DockEligibilityOptions {
  canAccessAdminDock: boolean;
  hasCurrentUser: boolean;
  isCommunitySwitcherEnabled: boolean;
  isDiscoverPage: boolean;
  isPromoLockedToPlansPage: boolean;
  shouldShowPromoLockedCommunitySwitcher: boolean;
}

const getDockEligibility = ({
  hasCurrentUser,
  isCommunitySwitcherEnabled,
  isDiscoverPage,
  isPromoLockedToPlansPage,
  canAccessAdminDock,
  shouldShowPromoLockedCommunitySwitcher,
}: DockEligibilityOptions) => {
  if (isDiscoverPage || !hasCurrentUser) return false;
  if (shouldShowPromoLockedCommunitySwitcher) return true;
  if (isPromoLockedToPlansPage) return false;

  return isCommunitySwitcherEnabled || canAccessAdminDock;
};

export const CircleAIAppShellLayoutDesktop = ({
  children,
  hasMinimalLayout = false,
  shouldDisplayHeader = true,
}: CircleAIAppShellLayoutProps) => {
  const pathname = useLocation({ select: location => location.pathname });
  const isAdminArea = useIsAdminArea();
  const isAdmin = useIsCommunityAdmin();
  const isAdminOrModerator = useIsAdminOrModerator();
  const { currentCommunity, currentCommunityMember, currentUser } =
    usePunditUserContext();
  const isAvatarsV2Layout = useIsAvatarsV2Layout();
  const shouldRenderAvatarPanel =
    isAvatarsV2Layout && !isAdmin && !!currentCommunityMember;
  const isInsideMessages = pathname.startsWith(chatRoomsPath());
  const isAvatarPanelFullscreen = useAvatarConversationPanelStore(
    s => s.panelState === "fullscreen",
  );
  const {
    community_switcher_data: communitySwitcherData = [],
    display_community_switcher: isCommunitySwitcherVisible = false,
  } = useBackendProps();
  const isShellBypassed = useIsShellBypassed();
  const isPageChromeHidden = useIsPageChromeHidden();
  const isPromoLockedToPlansPage = useIsPromoLockedToPlansPage();
  const analyticsReportArtefactUuid = useCircleAiStore(
    s => s.analyticsReportArtefactUuid,
  );
  const closeAnalyticsReport = useCircleAiStore(s => s.closeAnalyticsReport);
  const isAdminDockNavVisible = useIsAdminDockNavVisible();
  const isDockNavEnabled = useIsCircleAIDockNavEnabled();
  const { isFullPageAiRoute, isAiRoute } = useIsCircleAiPageRoute();
  const { isDiscoverPage } = useRouteHelpers();
  // Must be called before the route-owned shell state so its layout effect sees
  // the new route before full-page AI routes force the panel closed.
  useCircleAiIntroCloseTrigger(currentUser?.id, isFullPageAiRoute);
  useRouteOwnedShellState({
    isAdmin,
    isAdminArea,
    isAdminOrModerator,
    isFullPageAiRoute,
    isAiRoute,
    isShellBypassed,
    pathname,
  });

  // `isAdminDockForced` is managed by the individual panel call sites
  // (ConversationPanelToggleButton/CloseButton, the community-switcher AI
  // toggle, the dock nav toggle) — no layout-level effect, so it can't
  // overwrite a call site's intent after render.
  const shouldRenderCanvasHeader =
    shouldDisplayHeader &&
    !hasMinimalLayout &&
    !isPageChromeHidden &&
    pathname !== twoFaPath();

  useAutoOpenPanelOnRailbarContent();
  useAvatarPanelUrlEntry();
  useAvatarPanelRouteState();

  const isCommunitySwitcherEnabled = Boolean(
    currentCommunity?.community_switcher_enabled,
  );
  const isCircleAiEnabled = useIsCircleAiEnabled();

  const canAccessAdminDock =
    (isAdmin && isCircleAiEnabled) ||
    (isAdminOrModerator && (isAdminDockNavVisible || isAdminArea));

  const shellVisibility = useShellReveal();
  const isOnboardingShell = shellVisibility === "hidden";

  // A locking coupon (`lock_to_plans_page` → `promotion_enabled`) pins the admin
  // to the plans page while on trial. Existing users may still switch to a
  // different community, but the locked community's own dock navigation stays
  // unavailable.
  const hasOtherSwitcherCommunities = communitySwitcherData.some(
    community => !community.is_current_community,
  );
  const shouldShowPromoLockedCommunitySwitcher =
    isPromoLockedToPlansPage &&
    isCommunitySwitcherVisible &&
    hasOtherSwitcherCommunities;

  const isDockEligible = getDockEligibility({
    canAccessAdminDock,
    hasCurrentUser: Boolean(currentUser),
    isCommunitySwitcherEnabled,
    isDiscoverPage,
    isPromoLockedToPlansPage,
    shouldShowPromoLockedCommunitySwitcher,
  });

  useDesktopDockState({
    isAdminArea,
    isAdminDockNavVisible,
    isDockEligible,
    shouldCollapseForPromoLockedCommunitySwitcher:
      shouldShowPromoLockedCommunitySwitcher,
  });

  return (
    <MobileDrawerProvider renderLeftSidebar={undefined}>
      <StoreConnectedShellLayout>
        <CanvasHeaderProvider>
          <ShellChromeBanners />
          <PlanTrialTopBanner />
          {!isOnboardingShell && <TrialBannerV2 />}
          <AppBanner />
          <CircleAIAppShell.Layout.ShellBody>
            {isDockEligible && (
              <DesktopDock
                hidden={isOnboardingShell}
                isAdmin={isAdmin}
                isCircleAiEnabled={isCircleAiEnabled}
                isDockNavEnabled={isDockNavEnabled}
                isPromoLockedToPlansPage={isPromoLockedToPlansPage}
              />
            )}
            <CircleAIAppShell.Layout.ConversationPanel
              aria-label={t("circle_ai_shell.conversation_panel")}
              resizeAriaLabel={t("circle_ai_shell.resize_conversation")}
            >
              <AiConversationPanel />
            </CircleAIAppShell.Layout.ConversationPanel>
            <CircleAIAppShell.Layout.Canvas
              aria-label={t("circle_ai_shell.canvas")}
            >
              <ErrorBoundary>
                <div
                  inert={isAvatarPanelFullscreen}
                  className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
                >
                  {isOnboardingShell && <OnboardingHeader />}
                  {isShellBypassed ? (
                    <>
                      {shouldRenderCanvasHeader && <HeaderV3 />}
                      <CanvasOnlyContent>{children}</CanvasOnlyContent>
                    </>
                  ) : (
                    <ShellNativeContent>{children}</ShellNativeContent>
                  )}
                  {analyticsReportArtefactUuid && (
                    <Suspense
                      fallback={
                        <AnalyticsReportCanvasSkeleton
                          onClose={closeAnalyticsReport}
                        />
                      }
                    >
                      <AnalyticsReportCanvas
                        artefactUuid={analyticsReportArtefactUuid}
                      />
                    </Suspense>
                  )}
                </div>
              </ErrorBoundary>
            </CircleAIAppShell.Layout.Canvas>
            {shouldRenderAvatarPanel && (
              <AvatarPanelRoot
                aria-label={t("circle_ai_shell.conversation_panel")}
                resizeAriaLabel={t("circle_ai_shell.resize_conversation")}
              >
                <AvatarConversationPanel />
              </AvatarPanelRoot>
            )}
          </CircleAIAppShell.Layout.ShellBody>
          {shouldRenderAvatarPanel && !isInsideMessages && (
            <AvatarChatActivitySync />
          )}
          <LatestAvatarChatButton />
        </CanvasHeaderProvider>
      </StoreConnectedShellLayout>
    </MobileDrawerProvider>
  );
};
